import type { Message } from '../../../i18n/types';

/**
 * Texts of the automation project pages. Spread into the app dictionaries
 * (i18n/en.ts, i18n/ru.ts); keys start with `automation.project.`.
 */
export const automationProjectEn = {
  // ─── home ───
  'automation.project.home.createTeam': 'New team',
  'automation.project.home.accounts': 'Test accounts',
  'automation.project.home.fullRun': 'Full run',
  'automation.project.home.teamsHint':
    'A team owns its autotest projects. Open a project for its runs, test catalogue and test data.',
  'automation.project.home.noActiveRunsHint': 'Runs start from a project.',
  'automation.project.home.searchTeams': 'Search teams…',
  'automation.project.home.searchTeamsLabel': 'Search teams',
  'automation.project.home.noTeamsFound': 'No teams match «{query}»',
  'automation.project.teamProjects': { one: '{count} project', other: '{count} projects' },
  'automation.project.teamMembers': { one: '{count} member', other: '{count} members' },
  'automation.project.activeCount': 'Running: {count}',

  // ─── create team ───
  'automation.project.createTeam.title': 'New team',
  'automation.project.createTeam.name': 'Team name',
  'automation.project.createTeam.namePlaceholder': 'e.g. Payments QA',
  'automation.project.createTeam.descriptionPlaceholder': 'What the team is responsible for',
  'automation.project.createTeam.members': 'Responsible people',
  'automation.project.createTeam.membersPlaceholder': 'Names, comma-separated',
  'automation.project.createTeam.projects': 'Projects',
  'automation.project.createTeam.addProject': 'Project',
  'automation.project.createTeam.projectN': 'Project {n}',
  'automation.project.createTeam.removeProject': 'Remove project {n}',
  'automation.project.createTeam.projectsHint':
    'A project is created when it has a name, a GitLab project ID and a pipeline URL. Projects can also be added later inside the team; suites come from the repository scan or are added by hand.',
  'automation.project.createTeam.submit': 'Create team',
  'automation.project.createTeam.created': 'Team «{name}» created',

  // ─── project form fields ───
  'automation.project.field.name': 'Name',
  'automation.project.field.namePlaceholder': 'e.g. Loan',
  'automation.project.field.testType': 'Test type',
  'automation.project.field.descriptionPlaceholder': 'What the project covers',
  'automation.project.field.note': 'Note',
  'automation.project.field.notePlaceholder':
    'e.g. This repository holds several teams’ tests — pick your classes, do not run the whole set',
  'automation.project.field.noteHint':
    'Shown as a warning on the project page and in the run form. Empty — no warning.',
  'automation.project.field.gitlabProjectId': 'GitLab project ID',
  'automation.project.field.gitlabProjectIdHint': 'The repository scan works by it',
  'automation.project.field.jenkinsUrl': 'Jenkins URL (optional)',
  'automation.project.field.pipelineUrl': 'GitLab pipeline URL',
  'automation.project.field.triggerToken': 'Pipeline trigger token',
  'automation.project.field.triggerTokenSet':
    'Set. Leave empty to keep it, type a new one to replace it.',
  'automation.project.field.triggerTokenUnset':
    'Not set: pipelines start with the GitLab token of TMS.',
  'automation.project.field.triggerTokenRemove': 'Remove the token',
  'automation.project.field.triggerTokenRemoved': 'The token will be removed when you save.',

  // ─── add project ───
  'automation.project.addProject.title': 'Add project',
  'automation.project.addProject.modeNew': 'New project',
  'automation.project.addProject.modeCopy': 'From another team',
  'automation.project.addProject.mode': 'How to add',
  'automation.project.addProject.hint':
    'Suites for a new project are added by hand on the Test suites tab or come from the repository scan.',
  'automation.project.addProject.noForeign': 'Other teams have no projects to copy yet.',
  'automation.project.addProject.source': 'Project',
  'automation.project.addProject.sourcePlaceholder': '— choose a project —',
  'automation.project.addProject.sourceHint':
    'Settings and the test catalogue are copied whole — no need to enter the GitLab ID or scan the repository again',
  'automation.project.addProject.copyName': 'Name in your team',
  'automation.project.addProject.copyNamePlaceholder': 'as in the source project',
  'automation.project.addProject.copyNameHint':
    'Leave empty to keep the original. Change it only if the team already has a project with that name.',
  'automation.project.addProject.withTestData': 'Copy test data (phones, IINs, stands)',
  'automation.project.addProject.copy': 'Copy',
  'automation.project.addProject.added': 'Project «{name}» added',
  'automation.project.addProject.copied': '«{name}» copied: {classes} classes, {suites} suites',

  // ─── team page ───
  'automation.project.team.back': 'All teams',
  'automation.project.team.settings': 'Team settings',
  'automation.project.team.projects': 'Projects',
  'automation.project.team.noProjects': 'The team has no projects yet',
  'automation.project.team.noProjectsHint': 'A project is a GitLab repository with autotests.',
  'automation.project.team.gitlabProject': 'GitLab project {id}',
  'automation.project.team.hasNote': 'Has a note',
  'automation.project.team.open': 'Open',
  'automation.project.team.laterPipeline': 'Then a pipeline {trigger}: {status}, {when}',
  'automation.project.team.laterPipelineHint':
    'After the last run from here GitLab started a pipeline itself ({trigger}). It does not count towards the project status.',
  'automation.project.team.recentRuns': 'Recent team runs',
  'automation.project.repo': 'repo',
  'automation.project.repoConnected': 'repository connected',

  // ─── settings and deleting ───
  'automation.project.teamSettings.title': 'Team settings',
  'automation.project.teamSettings.membersHint':
    'Comma-separated. Existing members keep their roles.',
  'automation.project.teamSettings.saved': 'Team updated',
  'automation.project.teamSettings.dangerTitle': 'Delete the team',
  'automation.project.teamSettings.dangerText': 'All its projects and their data go with it.',
  'automation.project.teamSettings.delete': 'Delete team',
  'automation.project.teamSettings.impact':
    'To be deleted: {projects} projects with {suites} suites, {classes} classes, {data} test data entries and {runs} runs. This cannot be undone.',
  'automation.project.teamSettings.deleted': 'Team «{name}» deleted',
  'automation.project.settings.title': 'Project settings',
  'automation.project.settings.saved': 'Project updated',
  'automation.project.settings.dangerTitle': 'Delete the project',
  'automation.project.settings.dangerText':
    'The project and all its data will be deleted for good.',
  'automation.project.settings.delete': 'Delete project',
  'automation.project.settings.impact':
    'To be deleted: {suites} suites, {classes} classes, {data} test data entries and {runs} runs (with their history and Allure links). This cannot be undone.',
  'automation.project.settings.deleted': 'Project «{name}» deleted',
  'automation.project.danger.typeToConfirm': 'Type {name} to confirm',
  'automation.project.danger.deleteForever': 'Delete for good',
  'automation.project.danger.counting': 'Counting what will be deleted…',

  // ─── project page ───
  'automation.project.back': 'Back to the team',
  'automation.project.scan': 'Scan repository',
  'automation.project.run': 'Run',
  'automation.project.tabs': 'Project sections',
  'automation.project.tab.dashboard': 'Dashboard',
  'automation.project.tab.suites': 'Test suites',
  'automation.project.tab.classes': 'Test classes',
  'automation.project.tab.data': 'Test data',
  'automation.project.tab.readiness': 'Readiness',

  // ─── dashboard ───
  'automation.project.dashboard.passRate': 'Pass rate',
  'automation.project.dashboard.passRateHint': {
    one: '{passed} passed · {red} failed · {count} run',
    other: '{passed} passed · {red} failed · {count} runs',
  },
  'automation.project.dashboard.noFinished': 'No finished runs with tests',
  'automation.project.dashboard.totalRuns': 'Runs',
  'automation.project.dashboard.lastResult': 'Last result',
  'automation.project.dashboard.active': 'Running',
  'automation.project.dashboard.lastRun': 'Last run {id}',
  'automation.project.dashboard.rerunFailed': 'Rerun failed',
  'automation.project.dashboard.rerunStarted': 'Rerun of the failed tests started',
  'automation.project.dashboard.openRun': 'Open run',
  'automation.project.dashboard.passed': 'Passed',
  'automation.project.dashboard.failed': 'Failed',
  'automation.project.dashboard.broken': 'Broken',
  'automation.project.dashboard.skipped': 'Skipped',
  'automation.project.dashboard.duration': 'Duration',
  'automation.project.dashboard.allure': 'Allure report',
  'automation.project.dashboard.inProgress': 'Running…',
  'automation.project.dashboard.cancelledNoResult':
    'The run was cancelled — the test totals are incomplete; see the run page.',
  'automation.project.dashboard.noTests':
    'GitLab’s test report has no tests — open the run or the pipeline.',
  'automation.project.dashboard.causesSubtitle': 'run {id}: {red} red of {total}',
  'automation.project.dashboard.allTests': 'All tests of the run',
  'automation.project.dashboard.history': 'Run history',
  'automation.project.dashboard.filterAll': 'All',
  'automation.project.dashboard.filterLabel': 'Show runs',
  'automation.project.dashboard.export': 'Export CSV',
  'automation.project.dashboard.exported': 'Runs exported to CSV',
  'automation.project.dashboard.noRunsForFilter': 'No runs with this status.',

  // ─── suites ───
  'automation.project.suites.hint':
    'The project’s test suites. They come from the repository (repo) or are added by hand.',
  'automation.project.suites.add': 'Add suite',
  'automation.project.suites.empty': 'The project has no test suites yet',
  'automation.project.suites.emptyHint':
    'Scan the repository to bring them in from the code, or add one by hand.',
  'automation.project.suites.addFirst': 'Add the first suite',
  'automation.project.suites.sourceRepo': 'repo',
  'automation.project.suites.sourceManual': 'manual',
  'automation.project.suites.delete': 'Delete suite {name}',
  'automation.project.suites.deleteTitle': 'Delete the suite?',
  'automation.project.suites.deleteDescription':
    'The suite «{name}» will be removed from the project.',
  'automation.project.suites.deleted': 'Suite deleted',
  'automation.project.suites.dialogTitle': 'Add a test suite',
  'automation.project.suites.namePlaceholder': 'e.g. Refunds',
  'automation.project.suites.descriptionPlaceholder': 'What the suite covers',
  'automation.project.suites.added': 'Suite «{name}» added',

  // ─── classes ───
  'automation.project.classes.all': 'All',
  'automation.project.classes.tagFilter': 'Filter by tag',
  'automation.project.classes.selected': '{count} selected',
  'automation.project.classes.runSelected': 'Run selected ({count})',
  'automation.project.classes.selectAll': 'Select all shown classes',
  'automation.project.classes.select': 'Select {name}',
  'automation.project.classes.scenario': 'Scenario',
  'automation.project.classes.suite': 'Suite',
  'automation.project.classes.tests': 'Tests',
  'automation.project.classes.tags': 'Tags',
  'automation.project.classes.addNote': 'Add a note to {name}',
  'automation.project.classes.editNote': 'Edit the note of {name}',
  'automation.project.classes.run': 'Run',
  'automation.project.classes.empty': 'No test classes yet',
  'automation.project.classes.emptyHint': 'Scan the repository to bring them in.',
  'automation.project.classes.noteTitle': 'Note · {name}',
  'automation.project.classes.noteText': 'Text',
  'automation.project.classes.notePlaceholder':
    'e.g. needs a fresh account: after a run the account lands in the stop list and a second run fails',
  'automation.project.classes.noteHint':
    'Everyone who checks the data before running this class sees it. An empty field removes the note.',
  'automation.project.classes.noteSaved': 'Note saved',
  'automation.project.classes.noteRemoved': 'Note removed',

  // ─── test data ───
  'automation.project.data.title': 'Test data',
  'automation.project.data.hint':
    'Environment parameters and test accounts. They apply to the next runs.',
  'automation.project.data.history': 'Change history',
  'automation.project.data.import': 'Import',
  'automation.project.data.add': 'Add',
  'automation.project.data.key': 'Name',
  'automation.project.data.value': 'Value',
  'automation.project.data.ci': 'CI / source',
  'automation.project.data.changed': 'Changed',
  'automation.project.data.changedBy': '{when} · {by}',
  'automation.project.data.wiredInCi': '.gitlab-ci.yml reads it as its own variable',
  'automation.project.data.keyPlaceholder': 'PHONE_4',
  'automation.project.data.valuePlaceholder': '+70000000004',
  'automation.project.data.edit': 'Edit {key}',
  'automation.project.data.delete': 'Delete {key}',
  'automation.project.data.saved': 'Saved. The next runs will use it.',
  'automation.project.data.added': 'Parameter added',
  'automation.project.data.deleteTitle': 'Delete the parameter?',
  'automation.project.data.deleteDescription':
    '«{key}» will be removed from the project’s test data; the change history keeps a record.',
  'automation.project.data.deleted': '«{key}» deleted',
  'automation.project.data.empty': 'No test data yet',
  'automation.project.data.importTitle': 'Bulk import',
  'automation.project.data.importHint':
    'Paste key–value pairs, one per line. Separator: =, a comma or a tab.',
  'automation.project.data.importLabel': 'Pairs',
  'automation.project.data.importSubmit': 'Import',
  'automation.project.data.imported': {
    one: '{count} parameter imported',
    other: '{count} parameters imported',
  },
  'automation.project.data.historyEmpty': 'No changes yet.',
  'automation.project.data.action.created': 'created',
  'automation.project.data.action.updated': 'updated',
  'automation.project.data.action.deleted': 'deleted',

  // ─── readiness ───
  'automation.project.readiness.view': 'View',
  'automation.project.readiness.byClasses': 'By classes',
  'automation.project.readiness.byAccounts': 'By accounts',
  'automation.project.readiness.onlyWithRequires': 'only classes with requirements',
  'automation.project.readiness.legend':
    'Requirements — requires in testdata; capabilities — the account’s last check',
  'automation.project.readiness.info':
    'An account goes into a test when the run starts: New run → pick classes → Run → in the check window, the «Account from the pool» field of a class group. Fitting accounts are on top, with ✓; phone, IIN and BIN fill in by themselves. A card left blocked, no main or second account — «Repair» at the account in an expanded row. Classes that need a new account on every run get an unused one at the start; after the run it is marked used up.',
  'automation.project.readiness.stale':
    '{stale} of {total} accounts are not checked for capabilities yet — check them all on the accounts page.',
  'automation.project.readiness.openAccounts': 'Test accounts',
  'automation.project.readiness.notScanned':
    'The project was never scanned — scan the repository to see what its classes need.',
  'automation.project.readiness.emptyPool':
    'The pool is empty — add accounts on the accounts page.',
  'automation.project.readiness.noRequires':
    'The project’s classes have no requires in testdata. Untick the box to see all classes, or describe the requirements in the repository and scan the project again.',
  'automation.project.readiness.noClasses':
    'The project plan has no classes — scan the repository again.',
  'automation.project.readiness.colNeeds': 'What is needed · classes',
  'automation.project.readiness.colFit': 'Fit',
  'automation.project.readiness.colNoFit': 'Do not fit',
  'automation.project.readiness.noRequirements': 'no requirements',
  'automation.project.readiness.fresh': 'a new account on every run',
  'automation.project.readiness.freshHint':
    'After the run the account it got is marked used up for this class',
  'automation.project.readiness.unknownKeys': 'unknown keys: {keys}',
  'automation.project.readiness.fitOf': 'of {total}',
  'automation.project.readiness.fitOfType': 'of {total} {type}',
  'automation.project.readiness.verdict.ok': 'fits',
  'automation.project.readiness.verdict.spent': 'used up by this class',
  'automation.project.readiness.verdict.missing': 'lacks: {list}',
  'automation.project.readiness.verdict.unchecked': 'not checked',
  'automation.project.readiness.verdict.unknown': 'could not check: {list}',
  'automation.project.readiness.unspend': 'Back in use',
  'automation.project.readiness.unspendHint': 'Remove the «used up» mark for this class',
  'automation.project.readiness.unspendTitle': 'Put «{account}» back in use for {className}?',
  'automation.project.readiness.unspendDescription':
    'The class may get this account as a new one again — do it only if the account is back in its original state (for example, the card application was processed).',
  'automation.project.readiness.unspent': '«{account}» is back in use for {className}',
  'automation.project.readiness.repairTitle': '{repair} — «{account}»?',
  'automation.project.readiness.repairAfter': 'After the repair the account re-checks itself.',
  'automation.project.readiness.repairConfirm': 'Repair',
  'automation.project.readiness.repairStarted': 'Repair started: {repair}',
  'automation.project.readiness.repairWith': '{detail}. Repair: {repair}',
  'automation.project.readiness.repairWarn.openCard':
    'Unblocks blocked corporate cards; waits up to 3 minutes for the status.',
  'automation.project.readiness.repairWarn.mainAccount':
    'The first open tenge account becomes the main one. No OTP.',
  'automation.project.readiness.repairWarn.twoAccounts':
    'Opens a real tenge account on the stand, signed with OTP.',
  'automation.project.readiness.repairWarn.loanPayFunds':
    'A transfer from another own tenge account to the loan repayment account up to the scheduled payment, signed with OTP.',
  'automation.project.readiness.repairWarn.loanPartialFunds':
    'A transfer from another own tenge account to the loan repayment account up to the partial early repayment minimum, signed with OTP.',
  'automation.project.readiness.colType': 'Type',
  'automation.project.readiness.colAccount': 'Account',
  'automation.project.readiness.colChecked': 'Checked',
  'automation.project.readiness.notChecked': 'not checked',
  'automation.project.readiness.noLabel': '{type} without a label',
  'automation.project.readiness.repairing': '{title}…',

  // ─── scan ───
  'automation.project.scan.title': 'Repository scan',
  'automation.project.scan.project': 'Project',
  'automation.project.scan.type': 'Type',
  'automation.project.scan.intro':
    'TMS clones the repository and runs the scanners: testdata.json (the CaseData model), System.getProperty parameters, @Tag domains and test classes. Before importing you see exactly what will change.',
  'automation.project.scan.scanning': 'Scanning…',
  'automation.project.scan.steps': 'git clone → gradle parse → scan params / tests / testdata…',
  'automation.project.scan.failed': 'The scan failed — see the log.',
  'automation.project.scan.done': 'Repository scanned. Data source: {source}',
  'automation.project.scan.log': 'Scan log',
  'automation.project.scan.found': 'found {count}',
  'automation.project.scan.new': '+{count} new',
  'automation.project.scan.updated': '{count} changed',
  'automation.project.scan.removed': '−{count} to remove',
  'automation.project.scan.unchanged': 'no changes',
  'automation.project.scan.itemNew': 'new',
  'automation.project.scan.itemUpdated': 'upd',
  'automation.project.scan.itemRemoved': 'del',
  'automation.project.scan.more': '…and {count} more',
  'automation.project.scan.applyHint': 'The ticked sections will be imported into the project.',
  'automation.project.scan.noDiff': 'No differences with the repository.',
  'automation.project.scan.apply': 'Import',
  'automation.project.scan.applied': {
    one: '{count} section imported. The project data is updated.',
    other: '{count} sections imported. The project data is updated.',
  },
} as const;

export const automationProjectRu: Record<keyof typeof automationProjectEn, Message> = {
  // ─── home ───
  'automation.project.home.createTeam': 'Создать команду',
  'automation.project.home.accounts': 'Аккаунты',
  'automation.project.home.fullRun': 'Полный прогон',
  'automation.project.home.teamsHint':
    'У команды свои проекты автотестов. В проекте — запуски, каталог тестов и тестовые данные.',
  'automation.project.home.noActiveRunsHint': 'Запуск — из проекта.',
  'automation.project.home.searchTeams': 'Поиск команд…',
  'automation.project.home.searchTeamsLabel': 'Поиск команд',
  'automation.project.home.noTeamsFound': 'Команд по запросу «{query}» нет',
  'automation.project.teamProjects': {
    one: '{count} проект',
    few: '{count} проекта',
    many: '{count} проектов',
    other: '{count} проекта',
  },
  'automation.project.teamMembers': {
    one: '{count} участник',
    few: '{count} участника',
    many: '{count} участников',
    other: '{count} участника',
  },
  'automation.project.activeCount': 'Выполняется: {count}',

  // ─── create team ───
  'automation.project.createTeam.title': 'Создать команду',
  'automation.project.createTeam.name': 'Название команды',
  'automation.project.createTeam.namePlaceholder': 'напр. Payments QA',
  'automation.project.createTeam.descriptionPlaceholder': 'Зона ответственности команды',
  'automation.project.createTeam.members': 'Ответственные сотрудники',
  'automation.project.createTeam.membersPlaceholder': 'Имена через запятую',
  'automation.project.createTeam.projects': 'Проекты',
  'automation.project.createTeam.addProject': 'Проект',
  'automation.project.createTeam.projectN': 'Проект {n}',
  'automation.project.createTeam.removeProject': 'Убрать проект {n}',
  'automation.project.createTeam.projectsHint':
    'Проект создаётся, если у него есть название, GitLab Project ID и Pipeline URL. Проекты можно добавить и позже — внутри команды; наборы тестов подтянутся из репозитория или добавляются вручную.',
  'automation.project.createTeam.submit': 'Создать команду',
  'automation.project.createTeam.created': 'Команда «{name}» создана',

  // ─── project form fields ───
  'automation.project.field.name': 'Название',
  'automation.project.field.namePlaceholder': 'напр. Loan',
  'automation.project.field.testType': 'Тип тестов',
  'automation.project.field.descriptionPlaceholder': 'Что покрывает проект',
  'automation.project.field.note': 'Примечание',
  'automation.project.field.notePlaceholder':
    'напр. В проекте тесты нескольких команд — выберите свои классы, весь набор целиком не запускайте',
  'automation.project.field.noteHint':
    'Предупреждением на странице проекта и в форме запуска. Пустое поле — предупреждения нет.',
  'automation.project.field.gitlabProjectId': 'GitLab Project ID',
  'automation.project.field.gitlabProjectIdHint': 'По нему работает сканирование репозитория',
  'automation.project.field.jenkinsUrl': 'Jenkins URL (опц.)',
  'automation.project.field.pipelineUrl': 'GitLab Pipeline URL',
  'automation.project.field.triggerToken': 'Токен запуска пайплайна',
  'automation.project.field.triggerTokenSet':
    'Задан. Оставьте поле пустым, чтобы не менять, или введите новый.',
  'automation.project.field.triggerTokenUnset':
    'Не задан: пайплайны запускаются GitLab-токеном TMS.',
  'automation.project.field.triggerTokenRemove': 'Удалить токен',
  'automation.project.field.triggerTokenRemoved': 'Токен удалится при сохранении.',

  // ─── add project ───
  'automation.project.addProject.title': 'Добавить проект',
  'automation.project.addProject.modeNew': 'Новый проект',
  'automation.project.addProject.modeCopy': 'Из другой команды',
  'automation.project.addProject.mode': 'Как добавить',
  'automation.project.addProject.hint':
    'Наборы тестов для нового проекта можно добавить вручную во вкладке «Наборы тестов», либо они подтянутся при сканировании репозитория.',
  'automation.project.addProject.noForeign':
    'В других командах пока нет проектов, которые можно скопировать.',
  'automation.project.addProject.source': 'Проект',
  'automation.project.addProject.sourcePlaceholder': '— выберите проект —',
  'automation.project.addProject.sourceHint':
    'Настройки и каталог тестов копируются целиком — вводить GitLab ID и сканировать репозиторий заново не нужно',
  'automation.project.addProject.copyName': 'Название в вашей команде',
  'automation.project.addProject.copyNamePlaceholder': 'как в исходном проекте',
  'automation.project.addProject.copyNameHint':
    'Оставьте пустым, чтобы взять исходное. Менять нужно, только если такое название в команде уже занято.',
  'automation.project.addProject.withTestData': 'Скопировать тестовые данные (номера, ИИН, стенды)',
  'automation.project.addProject.copy': 'Скопировать',
  'automation.project.addProject.added': 'Проект «{name}» добавлен',
  'automation.project.addProject.copied':
    '«{name}» скопирован: классов — {classes}, наборов — {suites}',

  // ─── team page ───
  'automation.project.team.back': 'Все команды',
  'automation.project.team.settings': 'Настройки команды',
  'automation.project.team.projects': 'Проекты',
  'automation.project.team.noProjects': 'У команды пока нет проектов',
  'automation.project.team.noProjectsHint': 'Проект — это репозиторий GitLab с автотестами.',
  'automation.project.team.gitlabProject': 'GitLab-проект {id}',
  'automation.project.team.hasNote': 'Есть примечание',
  'automation.project.team.open': 'Открыть',
  'automation.project.team.laterPipeline': 'Потом пайплайн {trigger}: {status}, {when}',
  'automation.project.team.laterPipelineHint':
    'После последнего запуска отсюда GitLab сам запустил пайплайн ({trigger}). В статус проекта он не входит.',
  'automation.project.team.recentRuns': 'Последние запуски команды',
  'automation.project.repo': 'repo',
  'automation.project.repoConnected': 'repo подключён',

  // ─── settings and deleting ───
  'automation.project.teamSettings.title': 'Настройки команды',
  'automation.project.teamSettings.membersHint':
    'Через запятую. У существующих участников роли сохранятся.',
  'automation.project.teamSettings.saved': 'Команда обновлена',
  'automation.project.teamSettings.dangerTitle': 'Удаление команды',
  'automation.project.teamSettings.dangerText':
    'Вместе с командой удалятся все её проекты и их данные.',
  'automation.project.teamSettings.delete': 'Удалить команду',
  'automation.project.teamSettings.impact':
    'Будет удалено: проектов — {projects}, с ними наборов — {suites}, классов — {classes}, тестовых данных — {data}, запусков — {runs}. Действие необратимо.',
  'automation.project.teamSettings.deleted': 'Команда «{name}» удалена',
  'automation.project.settings.title': 'Настройки проекта',
  'automation.project.settings.saved': 'Проект обновлён',
  'automation.project.settings.dangerTitle': 'Удаление проекта',
  'automation.project.settings.dangerText': 'Проект и все его данные будут удалены безвозвратно.',
  'automation.project.settings.delete': 'Удалить проект',
  'automation.project.settings.impact':
    'Будет удалено: наборов — {suites}, классов — {classes}, параметров тестовых данных — {data}, запусков — {runs} (вместе с историей и ссылками на Allure). Действие необратимо.',
  'automation.project.settings.deleted': 'Проект «{name}» удалён',
  'automation.project.danger.typeToConfirm': 'Введите {name} для подтверждения',
  'automation.project.danger.deleteForever': 'Удалить безвозвратно',
  'automation.project.danger.counting': 'Считаем, что удалится…',

  // ─── project page ───
  'automation.project.back': 'К команде',
  'automation.project.scan': 'Просканировать репозиторий',
  'automation.project.run': 'Запустить',
  'automation.project.tabs': 'Разделы проекта',
  'automation.project.tab.dashboard': 'Дашборд',
  'automation.project.tab.suites': 'Наборы тестов',
  'automation.project.tab.classes': 'Тест-классы',
  'automation.project.tab.data': 'Тестовые данные',
  'automation.project.tab.readiness': 'Готовность',

  // ─── dashboard ───
  'automation.project.dashboard.passRate': 'Pass Rate',
  'automation.project.dashboard.passRateHint': {
    one: '{passed} прошло · {red} упало · {count} запуск',
    few: '{passed} прошло · {red} упало · {count} запуска',
    many: '{passed} прошло · {red} упало · {count} запусков',
    other: '{passed} прошло · {red} упало · {count} запуска',
  },
  'automation.project.dashboard.noFinished': 'нет завершённых запусков с тестами',
  'automation.project.dashboard.totalRuns': 'Всего запусков',
  'automation.project.dashboard.lastResult': 'Последний',
  'automation.project.dashboard.active': 'Активных',
  'automation.project.dashboard.lastRun': 'Последний запуск {id}',
  'automation.project.dashboard.rerunFailed': 'Перезапустить упавшие',
  'automation.project.dashboard.rerunStarted': 'Перезапуск упавших тестов начат',
  'automation.project.dashboard.openRun': 'Открыть запуск',
  'automation.project.dashboard.passed': 'Прошли',
  'automation.project.dashboard.failed': 'Упали',
  'automation.project.dashboard.broken': 'Сломаны',
  'automation.project.dashboard.skipped': 'Пропущены',
  'automation.project.dashboard.duration': 'Длительность',
  'automation.project.dashboard.allure': 'Allure-отчёт',
  'automation.project.dashboard.inProgress': 'Выполняется…',
  'automation.project.dashboard.cancelledNoResult':
    'Запуск отменён — итог тестов неполный, детали на странице запуска.',
  'automation.project.dashboard.noTests':
    'В отчёте о тестах GitLab нет ни одного теста — откройте запуск или пайплайн.',
  'automation.project.dashboard.causesSubtitle': 'запуск {id}: {red} красных из {total}',
  'automation.project.dashboard.allTests': 'Все тесты запуска',
  'automation.project.dashboard.history': 'История запусков',
  'automation.project.dashboard.filterAll': 'Все',
  'automation.project.dashboard.filterLabel': 'Какие запуски показать',
  'automation.project.dashboard.export': 'Выгрузить CSV',
  'automation.project.dashboard.exported': 'Запуски выгружены в CSV',
  'automation.project.dashboard.noRunsForFilter': 'Нет запусков по этому фильтру.',

  // ─── suites ───
  'automation.project.suites.hint':
    'Наборы тестов проекта. Подтягиваются из репозитория (repo) или добавляются вручную.',
  'automation.project.suites.add': 'Добавить набор',
  'automation.project.suites.empty': 'У проекта пока нет наборов тестов',
  'automation.project.suites.emptyHint':
    'Просканируйте репозиторий, чтобы подтянуть их из кода, либо добавьте вручную.',
  'automation.project.suites.addFirst': 'Добавить первый набор',
  'automation.project.suites.sourceRepo': 'repo',
  'automation.project.suites.sourceManual': 'вручную',
  'automation.project.suites.delete': 'Удалить набор {name}',
  'automation.project.suites.deleteTitle': 'Удалить набор?',
  'automation.project.suites.deleteDescription': 'Набор «{name}» будет удалён из проекта.',
  'automation.project.suites.deleted': 'Набор удалён',
  'automation.project.suites.dialogTitle': 'Добавить набор тестов',
  'automation.project.suites.namePlaceholder': 'напр. Refunds',
  'automation.project.suites.descriptionPlaceholder': 'Что покрывает набор',
  'automation.project.suites.added': 'Набор «{name}» добавлен',

  // ─── classes ───
  'automation.project.classes.all': 'Все',
  'automation.project.classes.tagFilter': 'Фильтр по тегу',
  'automation.project.classes.selected': 'Выбрано: {count}',
  'automation.project.classes.runSelected': 'Запустить выбранные ({count})',
  'automation.project.classes.selectAll': 'Выбрать все показанные классы',
  'automation.project.classes.select': 'Выбрать {name}',
  'automation.project.classes.scenario': 'Сценарий',
  'automation.project.classes.suite': 'Набор',
  'automation.project.classes.tests': 'Тесты',
  'automation.project.classes.tags': 'Теги',
  'automation.project.classes.addNote': 'Добавить примечание к {name}',
  'automation.project.classes.editNote': 'Изменить примечание к {name}',
  'automation.project.classes.run': 'Запуск',
  'automation.project.classes.empty': 'Тест-классов пока нет',
  'automation.project.classes.emptyHint': 'Просканируйте репозиторий, чтобы подтянуть их.',
  'automation.project.classes.noteTitle': 'Примечание · {name}',
  'automation.project.classes.noteText': 'Текст',
  'automation.project.classes.notePlaceholder':
    'напр. нужен свежий аккаунт: после прогона счёт попадает в стоп-лист и повторный запуск упадёт',
  'automation.project.classes.noteHint':
    'Увидят все, кто открывает проверку данных перед запуском этого класса. Пустое поле удаляет примечание.',
  'automation.project.classes.noteSaved': 'Примечание сохранено',
  'automation.project.classes.noteRemoved': 'Примечание удалено',

  // ─── test data ───
  'automation.project.data.title': 'Тестовые данные',
  'automation.project.data.hint':
    'Параметры окружения и тестовые аккаунты. Применяются к следующим запускам.',
  'automation.project.data.history': 'История изменений',
  'automation.project.data.import': 'Импорт',
  'automation.project.data.add': 'Добавить',
  'automation.project.data.key': 'Название',
  'automation.project.data.value': 'Значение',
  'automation.project.data.ci': 'CI / источник',
  'automation.project.data.changed': 'Изменён',
  'automation.project.data.changedBy': '{when} · {by}',
  'automation.project.data.wiredInCi': '.gitlab-ci.yml читает его как свою переменную',
  'automation.project.data.keyPlaceholder': 'PHONE_4',
  'automation.project.data.valuePlaceholder': '+70000000004',
  'automation.project.data.edit': 'Изменить {key}',
  'automation.project.data.delete': 'Удалить {key}',
  'automation.project.data.saved': 'Сохранено. Будет использовано в следующих запусках.',
  'automation.project.data.added': 'Параметр добавлен',
  'automation.project.data.deleteTitle': 'Удалить параметр?',
  'automation.project.data.deleteDescription':
    '«{key}» удалится из тестовых данных проекта; в истории изменений останется запись.',
  'automation.project.data.deleted': '«{key}» удалён',
  'automation.project.data.empty': 'Тестовых данных пока нет',
  'automation.project.data.importTitle': 'Массовый импорт',
  'automation.project.data.importHint':
    'Вставьте пары ключ-значение, по одной на строку. Разделитель: =, запятая или таб.',
  'automation.project.data.importLabel': 'Пары',
  'automation.project.data.importSubmit': 'Импортировать',
  'automation.project.data.imported': {
    one: 'Импортировано параметров: {count}',
    few: 'Импортировано параметров: {count}',
    many: 'Импортировано параметров: {count}',
    other: 'Импортировано параметров: {count}',
  },
  'automation.project.data.historyEmpty': 'Изменений пока нет.',
  'automation.project.data.action.created': 'добавлен',
  'automation.project.data.action.updated': 'изменён',
  'automation.project.data.action.deleted': 'удалён',

  // ─── readiness ───
  'automation.project.readiness.view': 'Вид',
  'automation.project.readiness.byClasses': 'По классам',
  'automation.project.readiness.byAccounts': 'По аккаунтам',
  'automation.project.readiness.onlyWithRequires': 'только классы с требованиями',
  'automation.project.readiness.legend':
    'Требования — requires в testdata, возможности — последняя проверка аккаунта',
  'automation.project.readiness.info':
    'Аккаунт в тест подставляется при запуске: «Новый запуск» → выберите классы → «Запустить» → в окне проверки у группы классов поле «Аккаунт из пула». Подходящие там сверху, с ✓; телефон, ИИН и БИН подставятся сами. Карта осталась заблокированной, нет основного или второго счёта — «Починить» у аккаунта в раскрытой строке. Классам «новый аккаунт на каждый прогон» при запуске выдаётся неиспользованный аккаунт, после запуска он помечается израсходованным.',
  'automation.project.readiness.stale':
    '{stale} из {total} аккаунтов ещё не проверены с возможностями — проверьте все на странице аккаунтов.',
  'automation.project.readiness.openAccounts': 'Аккаунты',
  'automation.project.readiness.notScanned':
    'Проект ещё не сканировали — просканируйте репозиторий, чтобы увидеть, что нужно классам.',
  'automation.project.readiness.emptyPool': 'Пул пуст — добавьте аккаунты на странице аккаунтов.',
  'automation.project.readiness.noRequires':
    'У классов проекта нет requires в testdata. Снимите галку, чтобы увидеть все классы, или опишите требования в репозитории и пересканируйте проект.',
  'automation.project.readiness.noClasses':
    'В плане проекта нет классов — пересканируйте репозиторий.',
  'automation.project.readiness.colNeeds': 'Что нужно · классы',
  'automation.project.readiness.colFit': 'Подходят',
  'automation.project.readiness.colNoFit': 'Не подходят',
  'automation.project.readiness.noRequirements': 'требований нет',
  'automation.project.readiness.fresh': 'новый аккаунт на каждый прогон',
  'automation.project.readiness.freshHint':
    'После запуска выданный аккаунт помечается израсходованным для этого класса',
  'automation.project.readiness.unknownKeys': 'неизвестные ключи: {keys}',
  'automation.project.readiness.fitOf': 'из {total}',
  'automation.project.readiness.fitOfType': 'из {total} {type}',
  'automation.project.readiness.verdict.ok': 'пойдёт',
  'automation.project.readiness.verdict.spent': 'израсходован этим классом',
  'automation.project.readiness.verdict.missing': 'нет: {list}',
  'automation.project.readiness.verdict.unchecked': 'не проверен',
  'automation.project.readiness.verdict.unknown': 'не удалось проверить: {list}',
  'automation.project.readiness.unspend': 'Вернуть в оборот',
  'automation.project.readiness.unspendHint': 'Снять отметку «израсходован» для этого класса',
  'automation.project.readiness.unspendTitle': 'Вернуть «{account}» в оборот для {className}?',
  'automation.project.readiness.unspendDescription':
    'Класс снова сможет получить этот аккаунт как новый — делайте так, только если аккаунт привели в исходное состояние (например, заявку на карту обработали).',
  'automation.project.readiness.unspent': '«{account}» снова в обороте для {className}',
  'automation.project.readiness.repairTitle': '{repair} — «{account}»?',
  'automation.project.readiness.repairAfter': 'После ремонта аккаунт перепроверится сам.',
  'automation.project.readiness.repairConfirm': 'Починить',
  'automation.project.readiness.repairStarted': 'Ремонт начат: {repair}',
  'automation.project.readiness.repairWith': '{detail}. Починить: {repair}',
  'automation.project.readiness.repairWarn.openCard':
    'Разблокировка заблокированных корпоративных карт; ждём статус до 3 минут.',
  'automation.project.readiness.repairWarn.mainAccount':
    'Первый открытый тенговый счёт станет основным. Без OTP.',
  'automation.project.readiness.repairWarn.twoAccounts':
    'На стенде откроется настоящий тенговый счёт, подпись OTP.',
  'automation.project.readiness.repairWarn.loanPayFunds':
    'Перевод с другого своего тенгового счёта на счёт для погашения кредита до суммы ЕП, подпись OTP.',
  'automation.project.readiness.repairWarn.loanPartialFunds':
    'Перевод с другого своего тенгового счёта на счёт для погашения кредита до минимума ЧДП, подпись OTP.',
  'automation.project.readiness.colType': 'Тип',
  'automation.project.readiness.colAccount': 'Аккаунт',
  'automation.project.readiness.colChecked': 'Проверен',
  'automation.project.readiness.notChecked': 'не проверен',
  'automation.project.readiness.noLabel': '{type} без ярлыка',
  'automation.project.readiness.repairing': '{title}…',

  // ─── scan ───
  'automation.project.scan.title': 'Сканирование репозитория',
  'automation.project.scan.project': 'Проект',
  'automation.project.scan.type': 'Тип',
  'automation.project.scan.intro':
    'Сервер склонирует репозиторий и запустит сканеры: testdata.json (модель CaseData), параметры System.getProperty, домены @Tag и тест-классы. Перед импортом покажем, что именно изменится.',
  'automation.project.scan.scanning': 'Сканирование…',
  'automation.project.scan.steps': 'git clone → gradle parse → scan params / tests / testdata…',
  'automation.project.scan.failed': 'Сканирование не удалось — смотрите лог.',
  'automation.project.scan.done': 'Репозиторий просканирован. Источник данных: {source}',
  'automation.project.scan.log': 'Лог сканирования',
  'automation.project.scan.found': 'найдено {count}',
  'automation.project.scan.new': '+{count} новых',
  'automation.project.scan.updated': 'изменено: {count}',
  'automation.project.scan.removed': '−{count} удалить',
  'automation.project.scan.unchanged': 'без изменений',
  'automation.project.scan.itemNew': 'new',
  'automation.project.scan.itemUpdated': 'upd',
  'automation.project.scan.itemRemoved': 'del',
  'automation.project.scan.more': '…и ещё {count}',
  'automation.project.scan.applyHint': 'Отмеченные разделы будут импортированы в проект.',
  'automation.project.scan.noDiff': 'Расхождений с репозиторием нет.',
  'automation.project.scan.apply': 'Импортировать',
  'automation.project.scan.applied': {
    one: 'Импортировано разделов: {count}. Данные проекта обновлены.',
    few: 'Импортировано разделов: {count}. Данные проекта обновлены.',
    many: 'Импортировано разделов: {count}. Данные проекта обновлены.',
    other: 'Импортировано разделов: {count}. Данные проекта обновлены.',
  },
};
